import axios from "axios";
import { signChallenge, parseKeyfile, encryptFile, ATT_CHUNK, attNonce, importAesKey,
  encryptAttChunk, bytesToHex } from "@/lib/crypto";
import { getRuntimeKeyfile } from "@/lib/vault";
import { tokGet, tokSet } from "@/lib/weblock";
import { installaMesh } from "@/lib/meshnet";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// RETE CHE NON SI SPEGNE: un solo indirizzo è un solo punto di rottura. La pazienza
// scende da 25 a 12 secondi perché non serve più aspettare l'inevitabile: la sentinella
// di `meshnet` tiene gli altri ingressi misurati e caldi, e su una lettura lenta ne
// affianca un secondo dopo 800 ms.
const client = axios.create({ baseURL: API, timeout: 12000 });
installaMesh(client, BACKEND_URL);

// attach JWT from storage on every request (unless a call sets its own auth header)
client.interceptors.request.use((config) => {
  const token = tokGet("lattice_token");
  if (token && !config.headers?.Authorization) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// public network data
export const getHealth = () => client.get("/health").then((r) => r.data);
export const getQbftStatus = () => client.get("/qbft/status").then((r) => r.data);
export const getBlocks = (limit) => client.get("/ledger/blocks", { params: limit ? { limit } : {} }).then((r) => r.data);

// Aggregated network overview, composed client-side from the live gateway
// endpoints (health + qbft/status + ledger/blocks) so the public pages work
// directly against the sovereign Rust backend without a server-side proxy.
// Mappa della rete: validatori del consenso e relay del mixnet.
export const networkPeers = () => client.get("/network/peers").then((r) => r.data);
// Nodi Sovrani: chi ha installato un nodo e si e' annunciato con la propria firma.
export const sovereignNodes = () => client.get("/public/nodes").then((r) => r.data);

export const getOverview = async () => {
  const [health, qbft, blocks] = await Promise.allSettled([
    getHealth(), getQbftStatus(), getBlocks(),
  ]);
  const h = health.status === "fulfilled" ? health.value : null;
  const q = qbft.status === "fulfilled" ? qbft.value : null;
  const b = blocks.status === "fulfilled" && Array.isArray(blocks.value) ? blocks.value : [];
  const chain = b.length ? {
    height: Math.max(...b.map((x) => x.index || 0)),
    blocks_count: b.length,
    total_transactions: b.reduce((s, x) => s + (x.transactions_count || 0), 0),
    chain_id: b[0].chain_id,
    signature_scheme: b[0].signed_with || "ML-DSA-65",
    latest_block_hash: b[0].block_hash,
    latest_proposer: b[0].proposer,
    latest_timestamp: b[0].timestamp,
  } : null;
  return { online: !!h, health: h, qbft: q, chain, blocks: b.slice(0, 10) };
};

// ── Auth (real backend · ML-DSA-65 challenge/verify) ──
export const getChallenge = (lns_name) =>
  client.post("/auth/challenge", { lns_name }).then((r) => r.data);
export const verifyAuth = (challenge_id, signature_hex) =>
  client.post("/auth/verify", { challenge_id, signature_hex }).then((r) => r.data);
export const fetchMe = () => client.get("/me").then((r) => r.data);

// Orchestrates the full keyfile login. Signs the challenge nonce locally,
// never transmitting the private key. Returns { token, lns, role, tenant_id, keyfile }.
export const loginWithKeyfileObject = async (keyfileObj) => {
  const kf = parseKeyfile(keyfileObj);
  if (!kf) throw new Error("File chiave non valido: manca lns_name o secret_key_hex");
  const chal = await getChallenge(kf.lns);
  const signature_hex = signChallenge(chal.nonce_hex, kf.sk);
  const sess = await verifyAuth(chal.challenge_id, signature_hex);
  return { ...sess, keyfile: keyfileObj };
};

// Submit the second factor (TOTP/PIN) using the short-lived pre-auth token.
export const secondFactor = (pre_token, payload) => client.post("/auth/second-factor", { pre_token, ...payload }).then((r) => r.data);
// Per-user (LNS) account security management.
export const getSecurity = () => client.get("/me/security").then((r) => r.data);
export const secTotpEnroll = () => client.post("/me/security/totp/enroll", {}).then((r) => r.data);
export const secTotpVerify = (code) => client.post("/me/security/totp/verify", { code }).then((r) => r.data);
export const secTotpDisable = (code) => client.post("/me/security/totp/disable", { code }).then((r) => r.data);
export const secPinSet = (pin) => client.post("/me/security/pin/set", { pin }).then((r) => r.data);
export const secPinDisable = (pin) => client.post("/me/security/pin/disable", { pin }).then((r) => r.data);
export const secSkip = () => client.post("/me/security/skip", {}).then((r) => r.data);

// LNS
export const lnsResolve = (q) =>
  client.get("/lns/resolve", { params: { q } }).then((r) => r.data);
export const lnsDirectory = () => client.get("/lns/directory").then((r) => r.data);

// employees
export const createEmployee = (payload) =>
  client.post("/users/employee", payload).then((r) => r.data);

// certify
export const certifyCounts = () => client.get("/certify/counts").then((r) => r.data);
export const certifyList = (folder) =>
  client.get("/certify/messages", { params: { folder } }).then((r) => r.data);
export const certifyGet = (id) => client.get(`/certify/messages/${id}`).then((r) => r.data);
export const certifySend = (payload) => client.post("/certify/send", payload).then((r) => r.data);
export const certifyNotes = () => client.get("/certify/notes").then((r) => r.data);
export const addNote = (payload) => client.post("/certify/notes", payload).then((r) => r.data);
export const delNote = (id) => client.delete(`/certify/notes/${id}`).then((r) => r.data);
export const certifyReceipt = (id, private_key) =>
  client.post(`/certify/messages/${id}/receipt`, { private_key }).then((r) => r.data);
export const certifyPatch = (id, payload) =>
  client.patch(`/certify/messages/${id}`, payload).then((r) => r.data);
export const certifyDelete = (id) =>
  client.delete(`/certify/messages/${id}`).then((r) => r.data);
export const uploadAttachment = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return client.post("/certify/attachments", fd).then((r) => r.data);
};
export const attachmentUrl = (key) => `${API}/certify/attachments/${key}`;

// certify public ledger
export const certifyLedger = (limit = 50) =>
  client.get("/ledger/certify-blocks", { params: { limit } }).then((r) => r.data);
export const certifyVerify = (q) =>
  client.get("/public/ledger/certify-blocks/verify", { params: { q } }).then((r) => r.data);

// roles (RBAC · ruoli espliciti + retrocompatibilità con i vecchi valori)
export const ROLE_LABELS = {
  sovereign: "Super Admin", super_admin: "Super Admin",
  admin: "Amministratore Azienda", company_admin: "Amministratore Azienda",
  compliance: "Compliance", auditor: "Auditor (sola lettura)",
  hr: "Risorse Umane", it: "IT / Sistemi",
  operator: "Utente", utente: "Utente", vendor: "Vendor",
};
export const ADMIN_ROLES = ["sovereign", "super_admin", "admin", "company_admin"];
// Ruoli che un Amministratore Azienda può assegnare ai propri dipendenti.
export const ASSIGNABLE_ROLES = [
  { value: "company_admin", label: "Amministratore Azienda" },
  { value: "compliance", label: "Compliance" },
  { value: "auditor", label: "Auditor (sola lettura)" },
  { value: "hr", label: "Risorse Umane" },
  { value: "it", label: "IT / Sistemi" },
  { value: "utente", label: "Utente" },
];
export const ROLE_TAG_COLOR = {
  sovereign: "warning", super_admin: "warning", admin: "warning", company_admin: "warning",
  compliance: "primary", it: "primary", hr: "primary",
  auditor: "neutral", operator: "neutral", utente: "neutral", vendor: "neutral",
};
export const isAdminRole = (role) => ADMIN_ROLES.includes(role);
export const roleLabel = (role) => ROLE_LABELS[role] || (role ? role[0].toUpperCase() + role.slice(1) : "Utente");
export const lnsSetRole = (payload) => client.post("/lns/set-role", payload).then((r) => r.data);
// API v1 · gestione chiavi (Amministratore Azienda)
export const apiKeysList = () => client.get("/lns/api-keys").then((r) => r.data);
export const apiKeysCreate = (name) => client.post("/lns/api-keys", { name }).then((r) => r.data);
export const apiKeysRevoke = (kid) => client.post("/lns/api-keys/revoke", { kid }).then((r) => r.data);

export const decodeJwt = (token) => {
  try {
    const p = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    return p;
  } catch { return null; }
};

// Build the in-app user object from the /auth/verify session.
export const buildUser = (sess) => {
  const lns = sess.lns || "";
  const local = lns.split("@")[0].split(".")[0] || lns;
  const nome = local ? local.charAt(0).toUpperCase() + local.slice(1) : "Utente";
  return {
    lns, role: sess.role, tenant_id: sess.tenant_id,
    label: roleLabel(sess.role), nome, cognome: "", display_name: lns,
  };
};
export const mediaUrl = (key) => (key ? `${API}/media/${key}` : null);
export const uploadMedia = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return client.post("/media", fd).then((r) => r.data);
};

// LNS advanced
// Key rotation & forced-rotation are served by the public sidecar (the strict main
// backend exposes neither). Authorized by ML-DSA-65 challenge/signature (proof of
// key possession) — the JWT secret is private to the Rust backend.
const sidecarChallenge = (lns_name) => client.post("/public/sign-challenge", { lns_name }).then((r) => r.data);
export const meContext = (lns_name) => client.get("/public/me/context", { params: { lns_name } }).then((r) => r.data);
// Caller rotates their own key: proves possession of the current key, gets a new keypair.
// v7.9.6 - HANDOVER SOVRANO. La rotazione del primo accesso non genera piu' una chiave
// casuale sul nodo: la chiave NASCE dalle 12 frasi sul dispositivo del dipendente e al
// nodo va la sola pubblica. La firma della sfida usa la chiave TEMPORANEA ricevuta
// dall'azienda (prova di possesso); da quel momento quella chiave non apre piu' niente,
// e chi l'aveva consegnata - il direttore compreso - resta fuori.
export const lnsRotateSovrana = async (lns_name, secret_key_hex_attuale, public_key_hex_nuova) => {
  const c = await sidecarChallenge(lns_name);
  const signature_hex = signChallenge(c.nonce_hex, secret_key_hex_attuale);
  return client
    .post("/public/rotate", { lns_name, nonce_id: c.nonce_id, signature_hex, public_key_hex: public_key_hex_nuova })
    .then((r) => r.data);
};
export const lnsRotate = async (lns_name, secret_key_hex) => {
  const c = await sidecarChallenge(lns_name);
  const signature_hex = signChallenge(c.nonce_hex, secret_key_hex);
  return client.post("/public/rotate", { lns_name, nonce_id: c.nonce_id, signature_hex }).then((r) => r.data);
};
// Admin forces an employee to rotate at first login (proves admin key possession).
export const lnsMarkRotate = async (admin_lns, admin_secret_key_hex, target_lns) => {
  const c = await sidecarChallenge(admin_lns);
  const signature_hex = signChallenge(c.nonce_hex, admin_secret_key_hex);
  return client.post("/public/lns/mark-rotate", { admin_lns, target_lns, nonce_id: c.nonce_id, signature_hex }).then((r) => r.data);
};
export const lnsRevoke = (target_lns) => client.post("/lns/revoke", { target_lns }).then((r) => r.data);
export const dnsStatus = () => client.get("/lns/dns/status").then((r) => r.data);
export const dnsVerify = (dns_domain) => client.post("/lns/dns/verify", { dns_domain }).then((r) => r.data);

// profile
export const updateProfile = (payload) => client.patch("/profile", payload).then((r) => r.data);

// pulse feed
export const pulseFeed = () => client.get("/pulse/feed").then((r) => r.data);
export const createPost = (payload) => client.post("/pulse/posts", payload).then((r) => r.data);
export const likePost = (id) => client.post(`/pulse/posts/${id}/like`).then((r) => r.data);
export const commentPost = (id, text) => client.post(`/pulse/posts/${id}/comment`, { text }).then((r) => r.data);
export const sharePost = (id) => client.post(`/pulse/posts/${id}/share`).then((r) => r.data);
export const votePoll = (id, option_id) => client.post(`/pulse/posts/${id}/vote`, { option_id }).then((r) => r.data);
export const rsvpEvent = (id, answer) => client.post(`/pulse/posts/${id}/rsvp`, { answer }).then((r) => r.data);
export const publicPost = (id) => client.get(`/pulse/public/posts/${id}`).then((r) => r.data);
export const publicPostUrl = (id) => `${window.location.origin}/post/${id}`;

// pulse chat
export const listConvs = () => client.get("/pulse/conversations").then((r) => r.data);
export const createConv = (payload) => client.post("/pulse/conversations", payload).then((r) => r.data);
export const convMessages = (id) => client.get(`/pulse/conversations/${id}/messages`).then((r) => r.data);
export const sendChat = (id, payload) => client.post(`/pulse/conversations/${id}/messages`, payload).then((r) => r.data);

// ── E2EE blob storage (chunked upload of opaque ciphertext) ──
const BLOB_CHUNK = 4 * 1024 * 1024; // 4MB
export const blobUpload = async (ciphertext, onProgress) => {
  const auth = await scAuth();
  const { upload_id } = await client.post("/public/blob/init", {}, { ...auth, timeout: 30000 }).then((r) => r.data);
  const total = Math.max(1, Math.ceil(ciphertext.length / BLOB_CHUNK));
  for (let i = 0; i < total; i++) {
    const chunk = ciphertext.subarray(i * BLOB_CHUNK, (i + 1) * BLOB_CHUNK);
    // copy into a standalone ArrayBuffer so axios sends exactly these bytes
    const body = chunk.slice().buffer;
    await client.post("/public/blob/chunk", body, {
      params: { upload_id, index: i },
      headers: { ...auth.headers, "Content-Type": "application/octet-stream" },
      timeout: 120000,
    });
    onProgress?.((i + 1) / total);
  }
  return client.post("/public/blob/finalize", { upload_id }, { ...auth, timeout: 60000 }).then((r) => r.data);
};
export const blobDownload = async (id) => {
  const auth = await scAuth();
  const r = await client.get(`/public/blob/${encodeURIComponent(id)}`, { responseType: "arraybuffer", timeout: 120000, ...auth });
  return new Uint8Array(r.data);
};
// Salva i pezzi in chiaro senza rimetterli in un unico array: il Blob li prende così.
export const saveParts = (parts, filename, mime) => {
  const blob = new Blob(parts, { type: mime || "application/octet-stream" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename || "allegato"; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
};
export const saveBytes = (bytes, filename, mime) => {
  const blob = new Blob([bytes], { type: mime || "application/octet-stream" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename || "allegato"; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
};

// ── Caricamento a pezzi: il file non entra mai tutto in memoria ──
// Si legge una fetta da 4 MB, si cifra, si spedisce come pezzo del blob. Un allegato da
// 100 MB occupa quindi 4 MB di memoria, non 300. Lo stesso formato lo scrive e lo legge
// anche l'app: `cs` e `n` nel manifest dicono al lettore come rimetterlo insieme.
export const blobInit = async () => {
  const auth = await scAuth();
  return client.post("/public/blob/init", {}, { ...auth, timeout: 30000 }).then((r) => r.data);
};
export const blobPutChunk = async (upload_id, index, bytes) => {
  const auth = await scAuth();
  await client.post("/public/blob/chunk", bytes.slice().buffer, {
    params: { upload_id, index },
    headers: { ...auth.headers, "Content-Type": "application/octet-stream" },
    timeout: 300000,
  });
};
export const blobFinalize = async (upload_id, allowed) => {
  const auth = await scAuth();
  return client.post("/public/blob/finalize", { upload_id, allowed: allowed || [] }, { ...auth, timeout: 120000 }).then((r) => r.data);
};

const uploadBigFile = async (file, onProgress) => {
  const keyRaw = crypto.getRandomValues(new Uint8Array(32));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const key = await importAesKey(bytesToHex(keyRaw), ["encrypt"]);
  const n = Math.max(1, Math.ceil(file.size / ATT_CHUNK));
  const { upload_id } = await blobInit();
  for (let i = 0; i < n; i++) {
    const slice = new Uint8Array(await file.slice(i * ATT_CHUNK, (i + 1) * ATT_CHUNK).arrayBuffer());
    await blobPutChunk(upload_id, i, await encryptAttChunk(key, iv, i, slice));
    onProgress?.((i + 1) / n);
  }
  const { id } = await blobFinalize(upload_id);
  return {
    id, name: file.name, mime: file.type || "application/octet-stream", size: file.size,
    key: bytesToHex(keyRaw), iv: bytesToHex(iv), cs: ATT_CHUNK, n,
  };
};

// Encrypt + upload a list of files; returns the attachment manifest (no plaintext leaves the browser).
export const uploadEncryptedFiles = async (files, onProgress) => {
  const manifest = [];
  for (let i = 0; i < files.length; i++) {
    const f = files[i];
    if (f.size > ATT_CHUNK) {
      manifest.push(await uploadBigFile(f, (p) => onProgress?.(i, p, files.length)));
      continue;
    }
    const enc = await encryptFile(f);
    const { id } = await blobUpload(enc.ciphertext, (p) => onProgress?.(i, p, files.length));
    manifest.push({ id, name: enc.name, mime: enc.mime, size: enc.size, key: enc.key, iv: enc.iv });
  }
  return manifest;
};

export const apiErr = (e) => e?.response?.data?.detail || e?.message || "Errore sconosciuto";

// ── Sidecar session + Certify PEC layer (privacy + delivery/read receipts + calendar) ──
// One ML-DSA-65 signature → short-lived sidecar session token (separate from the Rust JWT).
const scTokenValid = () => {
  const t = tokGet("lattice_sc_token");
  if (!t) return false;
  try {
    const p = JSON.parse(atob(t.split(".")[0].replace(/-/g, "+").replace(/_/g, "/")));
    return p.exp * 1000 > Date.now() + 30000;
  } catch { return false; }
};
export const sidecarLogin = async (lns_name, secret_key_hex) => {
  const c = await sidecarChallenge(lns_name);
  const signature_hex = signChallenge(c.nonce_hex, secret_key_hex);
  const r = await client.post("/public/session/verify", { lns_name, nonce_id: c.nonce_id, signature_hex }).then((x) => x.data);
  tokSet("lattice_sc_token", r.token);
  return r;
};
export const ensureSidecarSession = async () => {
  if (scTokenValid()) return tokGet("lattice_sc_token");
  const kf = getRuntimeKeyfile();
  const lns = kf?.lns_name || kf?.lns;
  const sk = kf?.secret_key_hex || kf?.private_key;
  if (!lns || !sk) throw new Error("Sessione sovrana non disponibile: sblocca la tua chiave (passphrase) o rientra con il tuo file chiave.");
  await sidecarLogin(lns, sk);
  return tokGet("lattice_sc_token");
};
const scAuth = async () => ({ headers: { Authorization: `Bearer ${await ensureSidecarSession()}` } });

export const scInbox = async (box = "inbox") => client.get("/public/certify/inbox", { params: { box }, ...(await scAuth()) }).then((r) => r.data);
export const scMessage = async (mail_id) => client.get("/public/certify/message", { params: { mail_id }, ...(await scAuth()) }).then((r) => r.data);
export const scCertTrash = async (mail_id) => client.post("/public/certify/trash", { mail_id }, await scAuth()).then((r) => r.data);
export const scCertRestore = async (mail_id) => client.post("/public/certify/restore", { mail_id }, await scAuth()).then((r) => r.data);
export const scCertDelete = async (mail_id) => client.post("/public/certify/delete", { mail_id }, await scAuth()).then((r) => r.data);
export const scReceipt = async (mail_id) => client.get("/public/certify/receipt", { params: { mail_id }, ...(await scAuth()) }).then((r) => r.data);
export const scNotes = async () => client.get("/public/certify/notes", await scAuth()).then((r) => r.data);
export const scAddNote = async (payload) => client.post("/public/certify/notes", payload, await scAuth()).then((r) => r.data);
export const scDelNote = async (id) => client.delete(`/public/certify/notes/${id}`, await scAuth()).then((r) => r.data);
export const scContacts = async () => client.get("/public/certify/contacts", await scAuth()).then((r) => r.data);
export const scDrafts = async () => client.get("/public/certify/drafts", await scAuth()).then((r) => r.data);
export const scSaveDraft = async (payload) => client.post("/public/certify/drafts", payload, await scAuth()).then((r) => r.data);
export const scDelDraft = async (id) => client.delete(`/public/certify/drafts/${id}`, await scAuth()).then((r) => r.data);
export const scNotifications = async () => client.get("/public/notifications", await scAuth()).then((r) => r.data);
export const getSidecarToken = () => ensureSidecarSession();

// RELAY A TEMPO: credenziali del TURN valide 10 minuti, emesse dal nodo solo a una
// sessione autenticata. Nel sorgente del sito non c'e' piu' nessuna credenziale.
export const turnBiglietto = async () =>
  (await client.get("/turn/biglietto", await scAuth())).data;
export const notificationsStreamUrl = (token) => `${API}/public/notifications/stream?token=${encodeURIComponent(token)}`;
// Sovereign console (lead inbox) — owner only
// v8.0.7 - era `@latticenetwork.lns`, cancellato nella v7.5.3: `isOwner()` era sempre
// falso e la console del proprietario non si apriva a nessuno.
export const OWNER_LNS = "fabioastorino@lattice.lns";
// Normalizza un LNS: garantisce l'estensione `.lns` (es. "mario@acme" o "mario@acme.it" -> "mario@acme.lns").
export const normalizeLns = (v) => {
  const s = (v || "").trim().toLowerCase();
  const at = s.indexOf("@");
  if (at <= 0 || at === s.length - 1) return s;
  const local = s.slice(0, at);
  const token = s.slice(at + 1).split(".")[0];
  if (!token) return s;
  return `${local}@${token}.lns`;
};
export const OWNER_LNS_LIST = [
  // v7.9.0: l'unico indirizzo del proprietario che esiste nel registro.
  "fabioastorino@lattice.lns",
  "gerardoastorino@lattice.lns",
];

// Il proprietario non è UN indirizzo: è l'elenco qui sotto. Un confronto secco con una
// stringa è ciò che ha tenuto spenta la console per settimane. Il potere vero non sta qui:
// ogni rotta riservata lo ricontrolla sul NODO, che legge `OWNER_LNS` dal suo ambiente.
export const isOwner = (user) =>
  !!user && (OWNER_LNS_LIST.includes(String(user.lns || "").toLowerCase()) || user.is_owner === true);
// Tutti gli indirizzi del proprietario: la striscia del canarino deve comparire su
// ciascuno. Non allarga i poteri di `isOwner` (che apre la console): è solo l'avviso.
export const isCanaryOwner = (user) =>
  !!user && (OWNER_LNS_LIST.includes(String(user.lns || "").toLowerCase()) || user.is_owner === true);
export const submitContactLead = (payload) => client.post("/public/lead", payload).then((r) => r.data);
export const scConsoleLeads = async () => client.get("/public/console/leads", await scAuth()).then((r) => r.data);
export const scConsoleStats = async () => client.get("/public/console/stats", await scAuth()).then((r) => r.data);
export const scConsoleLeadRead = async (id) => client.post(`/public/console/leads/${id}/read`, {}, await scAuth()).then((r) => r.data);
export const scConsoleLeadDelete = async (id) => client.delete(`/public/console/leads/${id}`, await scAuth()).then((r) => r.data);
export const scConsoleTenants = async () => client.get("/public/console/tenants", await scAuth()).then((r) => r.data);
export const scActivateLicense = async (payload) => client.post("/public/console/activate-license", payload, await scAuth()).then((r) => r.data);
export const scLicenseToggle = async (payload) => client.post("/public/console/license-toggle", payload, await scAuth()).then((r) => r.data);
export const scProvision = async (payload) => client.post("/public/console/provision", payload, await scAuth()).then((r) => r.data);
export const scProvisionHetzner = async (payload) => client.post("/public/console/provision-hetzner", payload, await scAuth()).then((r) => r.data);
export const scProvisionStatus = async (jobId) => client.get(`/public/console/provision/${jobId}`, await scAuth()).then((r) => r.data);
export const scHetznerStatus = async () => client.get("/public/console/hetzner-status", await scAuth()).then((r) => r.data);
export const scHetznerSaveToken = async (token) => client.post("/public/console/hetzner-token", { token }, await scAuth()).then((r) => r.data);
// RICERCA UTENTI. Sul Web mancava del tutto: la barra filtrava soltanto i contatti che
// avevi già, quindi cercare una persona nuova non dava mai niente.
export const scDirectorySearch = async (q) => client.get("/public/directory/search", { params: { q }, ...(await scAuth()) }).then((r) => r.data);
// RICHIESTE DI CONTATTO. Senza reciprocità non si può scrivere: la richiesta, con la sua
// presentazione cifrata per il destinatario, è l'unico ponte prima del sì.
export const scContactRequests = async () => client.get("/public/pulse/contact-requests", await scAuth()).then((r) => r.data);
export const scContactDecide = async (lns, action, silence) => client.post("/public/pulse/contact-requests/decide", { lns, action, silence: !!silence }, await scAuth()).then((r) => r.data);
export const scContactAddNote = async (lns, note_env) => client.post("/public/pulse/contacts/add", { lns, note_env: note_env || null }, await scAuth()).then((r) => r.data);
// VISIBILITÀ GLOBALE, per ogni tipo di account.
export const scVisibilityGet = async () => client.get("/public/pulse/visibility", await scAuth()).then((r) => r.data);
export const scVisibilitySet = async (enabled) => client.post("/public/pulse/visibility", { enabled: !!enabled }, await scAuth()).then((r) => r.data);
// Modifica di un quiz già pubblicato (parità con l'app).
export const scChannelPollUpdate = async (id, pid, payload) => client.post(`${CH(id)}/polls/${encodeURIComponent(pid)}/update`, payload, await scAuth()).then((r) => r.data);

export const scResolve = async (lns_list) => client.post("/public/directory/resolve", { lns_list }, await scAuth()).then((r) => r.data);
export const scPing = async () => client.post("/public/presence/ping", {}, await scAuth()).then((r) => r.data);
export const scSchedule = async (payload) => client.post("/public/certify/schedule", payload, await scAuth()).then((r) => r.data);
export const scScheduled = async () => client.get("/public/certify/scheduled", await scAuth()).then((r) => r.data);
export const scCancelScheduled = async (id) => client.delete(`/public/certify/scheduled/${id}`, await scAuth()).then((r) => r.data);

// ── Federazione P2P (sidecar) ──
export const scFedConfig = async () => client.get("/public/fed/config", await scAuth()).then((r) => r.data);
export const scFedSetConfig = async (node_url, name) => client.post("/public/fed/config", { node_url, name }, await scAuth()).then((r) => r.data);
export const scFedAddPeer = async (url) => client.post("/public/fed/peers", { url }, await scAuth()).then((r) => r.data);
// ISOLA o FEDERATA (v7.4.4): la scelta di un'azienda, non un'impostazione ereditata.
export const scFedSetMode = async (mode) => client.post("/public/fed/mode", { mode }, await scAuth()).then((r) => r.data);
export const scFedDelPeer = async (id) => client.delete(`/public/fed/peers/${id}`, await scAuth()).then((r) => r.data);

// ── Pulse (sidecar, E2EE) ──
export const scPulsePublishKey = async (kem_pk_hex) => client.post("/public/pulse/publish-key", { kem_pk_hex }, await scAuth()).then((r) => r.data);
export const scPulseKeys = async (lns_list) => client.post("/public/pulse/keys", { lns_list }, await scAuth()).then((r) => r.data);

// ── Chiamate voce (solo audio) · rendezvous anonimo ──
// Le chiavi X25519 servono a derivare il rid di discovery: il server non sa chi chiama chi.
export const scPulseDhKeys = async (lns_list) => client.post("/public/pulse/dhkeys", { lns_list }, await scAuth()).then((r) => r.data);
// I tre endpoint /public/anon/* non sono autenticati per costruzione: vedono solo rid
// derivati e buste cifrate. Aggiungere un token qui vorrebbe dire legare la chiamata a
// un'identita' lato server, cioe' esattamente quello che il rendezvous cieco evita.
export const anonPut = (rid, slot, data) => client.post("/public/anon/put", { rid, slot, data }).then((r) => r.data);
export const anonGet = (rid, ice_c_from = 0, ice_e_from = 0) => client.post("/public/anon/get", { rid, ice_c_from, ice_e_from }).then((r) => r.data);
export const anonPoll = (rids) => client.post("/public/anon/poll", { rids }).then((r) => r.data);
export const callRing = async (payload) => client.post("/public/pulse/call/ring", payload, await scAuth()).then((r) => r.data);
export const callAnswered = async (call_id) => client.post("/public/pulse/call/answered", { call_id }, await scAuth()).then((r) => r.data);
export const callReject = async (call_id) => client.post("/public/pulse/call/reject", { call_id }, await scAuth()).then((r) => r.data);
export const callHangup = async (call_id) => client.post("/public/pulse/call/hangup", { call_id }, await scAuth()).then((r) => r.data);
export const scPulseContacts = async () => client.get("/public/pulse/contacts", await scAuth()).then((r) => r.data);
export const scPulseAddContact = async (lns) => client.post("/public/pulse/contacts/add", { lns }, await scAuth()).then((r) => r.data);
export const scPulseDeleteConv = async (cid) => client.delete(`/public/pulse/conversations/${encodeURIComponent(cid)}`, await scAuth()).then((r) => r.data);
// `scope`: "me" cancella solo la mia copia, "all" la ritira anche dal destinatario.
// Senza ambito il server si comporta come prima (solo la mia copia).
/// RICEVUTA DI CUSTODIA (Modalità Ponte): «queste buste sono nella cassetta cifrata di
/// questo browser, potete distruggerle». Senza firma il server non cancella niente.
export const scPonteAck = async (conv_id, message_ids) =>
  client.post("/public/pulse/ponte/ack", { conv_id, message_ids }, await scAuth()).then((r) => r.data);
export const scPulseDeleteMsg = async (mid, scope) => client.delete(`/public/pulse/messages/${encodeURIComponent(mid)}`, { params: scope ? { scope } : {}, ...(await scAuth()) }).then((r) => r.data);
export const scPulsePinMsg = async (message_id, pin) => client.post("/public/pulse/message/pin", { message_id, pin: !!pin }, await scAuth()).then((r) => r.data);

// ── Gruppi (canali E2EE) — gli stessi endpoint usati dall'app mobile ──
export const scChannels = async () => client.get("/public/pulse/channels", await scAuth()).then((r) => r.data);
export const scChannelCreate = async (payload) => client.post("/public/pulse/channels", payload, await scAuth()).then((r) => r.data);
export const scChannelDetail = async (id) => client.get(`/public/pulse/channels/${encodeURIComponent(id)}`, await scAuth()).then((r) => r.data);
export const scChannelDelete = async (id) => client.delete(`/public/pulse/channels/${encodeURIComponent(id)}`, await scAuth()).then((r) => r.data);
export const scChannelLeave = async (id) => client.post(`/public/pulse/channels/${encodeURIComponent(id)}/leave`, {}, await scAuth()).then((r) => r.data);
export const scChannelPosts = async (id) => client.get(`/public/pulse/channels/${encodeURIComponent(id)}/posts`, await scAuth()).then((r) => r.data);
export const scChannelPost = async (id, payload) => client.post(`/public/pulse/channels/${encodeURIComponent(id)}/post`, payload, await scAuth()).then((r) => r.data);
export const scChannelRead = async (id) => client.post(`/public/pulse/channels/${encodeURIComponent(id)}/read`, {}, await scAuth()).then((r) => r.data);
export const scChannelMembers = async (id, skip) => client.get(`/public/pulse/channels/${encodeURIComponent(id)}/members`, { params: { skip: skip || 0 }, ...(await scAuth()) }).then((r) => r.data);
export const scChannelAddMembers = async (id, members) => client.post(`/public/pulse/channels/${encodeURIComponent(id)}/members`, { members }, await scAuth()).then((r) => r.data);
export const scChannelPostDelete = async (id, pid) => client.post(`/public/pulse/channels/${encodeURIComponent(id)}/posts/${encodeURIComponent(pid)}/delete`, {}, await scAuth()).then((r) => r.data);
// ── Account PRIVATO (gli stessi endpoint dell'app mobile) ──
export const personalCheck = (username) => client.get("/public/personal/check", { params: { username } }).then((r) => r.data);
export const personalSignup = (payload) => client.post("/public/personal/signup", payload).then((r) => r.data);

// Topic (le «modalità» del gruppo nascono da qui), quiz/sondaggi, ruoli e moderazione.
const CH = (id) => `/public/pulse/channels/${encodeURIComponent(id)}`;
export const scChannelTopics = async (id) => client.get(`${CH(id)}/topics`, await scAuth()).then((r) => r.data);
export const scChannelTopicCreate = async (id, name) => client.post(`${CH(id)}/topics`, { name }, await scAuth()).then((r) => r.data);
export const scChannelTopicDelete = async (id, tid) => client.post(`${CH(id)}/topics/${encodeURIComponent(tid)}/delete`, {}, await scAuth()).then((r) => r.data);
export const scChannelPolls = async (id) => client.get(`${CH(id)}/polls`, await scAuth()).then((r) => r.data);
export const scChannelPollCreate = async (id, payload) => client.post(`${CH(id)}/polls`, payload, await scAuth()).then((r) => r.data);
export const scChannelPollVote = async (id, pid, option) => client.post(`${CH(id)}/polls/${encodeURIComponent(pid)}/vote`, { option }, await scAuth()).then((r) => r.data);
export const scChannelPollClose = async (id, pid) => client.post(`${CH(id)}/polls/${encodeURIComponent(pid)}/close`, {}, await scAuth()).then((r) => r.data);
export const scChannelPollDelete = async (id, pid) => client.post(`${CH(id)}/polls/${encodeURIComponent(pid)}/delete`, {}, await scAuth()).then((r) => r.data);
export const scChannelQuizReset = async (id) => client.post(`${CH(id)}/quiz/reset`, {}, await scAuth()).then((r) => r.data);
export const scChannelSetRole = async (id, lns, role) => client.post(`${CH(id)}/role`, { lns, role }, await scAuth()).then((r) => r.data);
export const scChannelPending = async (id) => client.get(`${CH(id)}/pending`, await scAuth()).then((r) => r.data);
export const scChannelPostApprove = async (id, pid) => client.post(`${CH(id)}/posts/${encodeURIComponent(pid)}/approve`, {}, await scAuth()).then((r) => r.data);
export const scChannelPostReject = async (id, pid) => client.post(`${CH(id)}/posts/${encodeURIComponent(pid)}/reject`, {}, await scAuth()).then((r) => r.data);
export const scChannelReports = async (id) => client.get(`${CH(id)}/reports`, await scAuth()).then((r) => r.data);
export const scChannelPostLike = async (id, pid) => client.post(`${CH(id)}/posts/${encodeURIComponent(pid)}/like`, {}, await scAuth()).then((r) => r.data);
export const scChannelPollLike = async (id, pid) => client.post(`${CH(id)}/polls/${encodeURIComponent(pid)}/like`, {}, await scAuth()).then((r) => r.data);
export const scChannelPollLikes = async (id) => client.get(`${CH(id)}/poll-likes`, await scAuth()).then((r) => r.data);
export const scShareCreate = async (payload) => client.post(`/public/pulse/share`, payload, await scAuth()).then((r) => r.data);
export const scChannelPostLikes = async (id) => client.get(`${CH(id)}/post-likes`, await scAuth()).then((r) => r.data);
// ── Parità col telefono: reazioni, modifica, segnalazione, ordine delle modalità,
//    conferme di lettura. Stessi endpoint, stesse regole: il server non vede nulla in
//    chiaro, il testo modificato viene ricifrato dal browser con la chiave del gruppo.
export const scChannelPostReact = async (id, pid, emoji) => client.post(`${CH(id)}/posts/${encodeURIComponent(pid)}/react`, { emoji }, await scAuth()).then((r) => r.data);
export const scChannelReactions = async (id) => client.get(`${CH(id)}/reactions`, await scAuth()).then((r) => r.data);
export const scChannelPostEdit = async (id, pid, payload) => client.post(`${CH(id)}/posts/${encodeURIComponent(pid)}/edit`, payload, await scAuth()).then((r) => r.data);
export const scChannelPostReport = async (id, pid, reason) => client.post(`${CH(id)}/posts/${encodeURIComponent(pid)}/report`, { reason }, await scAuth()).then((r) => r.data);
export const scChannelTopicsReorder = async (id, order) => client.post(`${CH(id)}/topics/reorder`, { order }, await scAuth()).then((r) => r.data);
export const scChannelReads = async (id) => client.get(`${CH(id)}/reads`, await scAuth()).then((r) => r.data);
// Conferme di lettura: UNA impostazione per l'account, la stessa del telefono.
export const scPulsePrivacy = async () => client.get("/public/pulse/privacy", await scAuth()).then((r) => r.data);
export const scPulsePrivacySet = async (patch) => client.post("/public/pulse/privacy", patch, await scAuth()).then((r) => r.data);
export const scChannelConcorsoReminder = async (id, payload) => client.post(`${CH(id)}/concorso-reminder`, payload, await scAuth()).then((r) => r.data);
export const scChannelUpdate = async (id, payload) => client.post(`/public/pulse/channels/${encodeURIComponent(id)}/update`, payload, await scAuth()).then((r) => r.data);
export const scChannelMemberRemove = async (id, lns) => client.delete(`/public/pulse/channels/${encodeURIComponent(id)}/members/${encodeURIComponent(lns)}`, await scAuth()).then((r) => r.data);
export const scGroupsSearch = async (q) => client.get("/public/pulse/groups/search", { params: { q }, ...(await scAuth()) }).then((r) => r.data);
export const scChannelJoinRequest = async (id, message) => client.post(`/public/pulse/channels/${encodeURIComponent(id)}/join-request`, { message: message || "" }, await scAuth()).then((r) => r.data);
export const scChannelJoinRequests = async (id) => client.get(`/public/pulse/channels/${encodeURIComponent(id)}/join-requests`, await scAuth()).then((r) => r.data);
export const scChannelJoinApprove = async (id, lns, key_env) => client.post(`/public/pulse/channels/${encodeURIComponent(id)}/join-requests/${encodeURIComponent(lns)}/approve`, { key_env }, await scAuth()).then((r) => r.data);
export const scChannelJoinReject = async (id, lns) => client.post(`/public/pulse/channels/${encodeURIComponent(id)}/join-requests/${encodeURIComponent(lns)}/reject`, {}, await scAuth()).then((r) => r.data);

// ── Inviti con link (monouso e a scadenza) ──
export const scInviteCreate = async (id, payload) => client.post(`/public/pulse/channels/${encodeURIComponent(id)}/invites`, payload, await scAuth()).then((r) => r.data);
export const scInviteList = async (id) => client.get(`/public/pulse/channels/${encodeURIComponent(id)}/invites`, await scAuth()).then((r) => r.data);
export const scInviteShow = async (th) => client.get(`/public/pulse/invites/${encodeURIComponent(th)}`, await scAuth()).then((r) => r.data);
export const scInviteRedeem = async (th, key_env) => client.post(`/public/pulse/invites/${encodeURIComponent(th)}/redeem`, { key_env }, await scAuth()).then((r) => r.data);
export const scInviteRevoke = async (th) => client.post(`/public/pulse/invites/${encodeURIComponent(th)}/revoke`, {}, await scAuth()).then((r) => r.data);
export const scChannelPostPin = async (id, pid) => client.post(`/public/pulse/channels/${encodeURIComponent(id)}/posts/${encodeURIComponent(pid)}/pin`, {}, await scAuth()).then((r) => r.data);
export const scWebPushVapid = async () => client.get("/public/pulse/webpush/vapid").then((r) => r.data);
export const scWebPushSubscribe = async (payload) => client.post("/public/pulse/webpush/subscribe", payload, await scAuth()).then((r) => r.data);
export const scPulseBlocks = async () => client.get("/public/pulse/blocks", await scAuth()).then((r) => r.data);
export const scPulseBlockContact = async (lns, on) => client.post("/public/pulse/block", { lns, on: !!on }, await scAuth()).then((r) => r.data);
export const scPulseRemoveContact = async (lns) => client.delete(`/public/pulse/contacts/${encodeURIComponent(lns)}`, await scAuth()).then((r) => r.data);
export const scPulseConvs = async () => client.get("/public/pulse/conversations", await scAuth()).then((r) => r.data);
// Pallini in una sola risposta: {chat, certify}. Vedi UnreadContext del telefono.
// CRUSCOTTO DEI RELAY (v7.8.1): il foglio dei tre relay, solo a sessione valida.
// L'allarme invece è pubblico ma muto: dice "sì" o "no", non dice chi.
export const scRele = async () => client.get("/rete/rele", await scAuth()).then((r) => r.data);
export const scReleAllarme = () => client.get("/public/rete/rele/allarme").then((r) => r.data);
export const scBadges = async () => client.get("/public/pulse/badges", await scAuth()).then((r) => r.data);
// Butta dal server, per tutti, le buste che nessuno può più aprire.
export const scPurgeMessages = async (conv_id, message_ids) =>
  client.post("/public/pulse/purge", { conv_id, message_ids }, await scAuth()).then((r) => r.data);
// Avviso di prova a me stesso: dice quali canali hanno risposto e in quanti millisecondi.
export const scTestNotify = async () =>
  client.post("/public/pulse/test-notify", {}, await scAuth()).then((r) => r.data);
export const scPulseMessages = async (conv_id) => client.get("/public/pulse/messages", { params: { conv_id }, ...(await scAuth()) }).then((r) => r.data);
// Il browser come dispositivo: registro e chiavi usa-e-getta (solo le PUBBLICHE partono).
export const scRegisterDevice = async (device_id, label, platform, caps) =>
  client.post("/public/pulse/devices/register", { device_id, label, platform, caps: caps || [] }, await scAuth()).then((r) => r.data);
export const scPrekeysStatus = async (device_id) =>
  client.get(`/public/pulse/prekeys/status?device_id=${encodeURIComponent(device_id)}`, await scAuth()).then((r) => r.data);
export const scUploadPrekeys = async (device_id, keys) =>
  client.post("/public/pulse/prekeys/upload", { device_id, keys }, await scAuth()).then((r) => r.data);
export const scPulseSend = async (payload) => client.post("/public/pulse/send", payload, await scAuth()).then((r) => r.data);
export const scPulseRead = async (conv_id) => client.post("/public/pulse/read", { conv_id }, await scAuth()).then((r) => r.data);
// Profiles
export const scPulseMyProfile = async () => client.get("/public/pulse/profile", await scAuth()).then((r) => r.data);
export const scPulseSaveProfile = async (payload) => client.post("/public/pulse/profile", payload, await scAuth()).then((r) => r.data);
export const scPulseProfileOf = async (lns) => client.get("/public/pulse/profile-of", { params: { lns }, ...(await scAuth()) }).then((r) => r.data);
// Hub
export const scHubFeed = async () => client.get("/public/pulse/hub", await scAuth()).then((r) => r.data);
export const scHubCreate = async (payload) => client.post("/public/pulse/hub", payload, await scAuth()).then((r) => r.data);
export const scHubLike = async (id) => client.post(`/public/pulse/hub/${id}/like`, {}, await scAuth()).then((r) => r.data);
export const scHubReply = async (id, text) => client.post(`/public/pulse/hub/${id}/reply`, { text }, await scAuth()).then((r) => r.data);
export const scHubDelete = async (id) => client.delete(`/public/pulse/hub/${id}`, await scAuth()).then((r) => r.data);
export const hubPublicUrl = (id) => `${window.location.origin}/hub/${id}`;
export const scHubPublic = (id) => client.get(`/public/pulse/hub/public/${id}`).then((r) => r.data);
// Jobs
export const scJobs = async () => client.get("/public/pulse/jobs", await scAuth()).then((r) => r.data);
export const scJobCreate = async (payload) => client.post("/public/pulse/jobs", payload, await scAuth()).then((r) => r.data);
export const scJobDelete = async (id) => client.delete(`/public/pulse/jobs/${id}`, await scAuth()).then((r) => r.data);

// ════════════════════════════════════════════════════════════════════
// REAL BACKEND CONTRACTS (Fase 2) — /opt/lattice FastAPI
// ════════════════════════════════════════════════════════════════════

// ── Certify (server signs · ML-DSA-65) ──
export const certifyStats = () => client.get("/certify/stats").then((r) => r.data);
export const certifyRecent = (box = "all", limit = 100) =>
  client.get("/certify/recent", { params: { box, limit } }).then((r) => r.data);
export const certifySendReal = (payload) => client.post("/certify/send", payload).then((r) => r.data);
export const certifyRead = (id) => client.post(`/certify/${id}/read`).then((r) => r.data);
export const certifyRestore = (id) => client.post(`/certify/${id}/restore`).then((r) => r.data);
export const certifyTrashReal = (id) => client.delete(`/certify/${id}`).then((r) => r.data);
export const uploadCertifyAttachment = (file) => {
  const fd = new FormData(); fd.append("file", file);
  return client.post("/attachments/upload", fd).then((r) => r.data);
};
export const downloadBlob = async (path, filename) => {
  const r = await client.get(path, { responseType: "blob" });
  const url = URL.createObjectURL(r.data);
  const a = document.createElement("a");
  a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
};
export const downloadReceipt = (id) => downloadBlob(`/certify/${id}/receipt.pdf`, `ricevuta-${id}.pdf`);
export const downloadAttachment = (id, name) => downloadBlob(`/attachments/${id}`, name || id);

// ── Pulse (server-side E2E seal) ──
export const pulseConversations = () => client.get("/pulse/conversations").then((r) => r.data);
export const pulseMessages = (conv_id) => client.get("/pulse/messages", { params: { conv_id } }).then((r) => r.data);
export const pulseSend = (payload) => client.post("/pulse/send", payload).then((r) => r.data);
export const pulseSession = (conv_id) => client.get(`/pulse/session/${conv_id}`).then((r) => r.data);
// trial usage/status (hosted free-trial gating)
export const scTrialStatus = async () => client.get("/public/trial/status", await scAuth()).then((r) => r.data);

// ── LNS / Tenants ──
export const lnsActive = () => client.get("/lns/active").then((r) => r.data);
export const lnsCheck = (lns_name) => client.get("/lns/check", { params: { lns_name } }).then((r) => r.data);
export const lnsRegister = (payload) => client.post("/lns/register", payload).then((r) => r.data);
export const lnsRevokeReal = (payload) => client.post("/lns/revoke", payload).then((r) => r.data);
export const lnsUnlock = (lns_name) => client.post("/lns/unlock", { lns_name }).then((r) => r.data);
export const tenantsPublic = () => client.get("/tenants/public").then((r) => r.data);
export const tenantGet = (id) => client.get(`/tenants/${id}`).then((r) => r.data);
export const tenantEmployees = (id) => client.get(`/tenants/${id}/employees`).then((r) => r.data);
export const tenantStats = (id) => client.get(`/tenants/${id}/stats`).then((r) => r.data);

// ── Explorer / Ledger ──
export const explorerStats = () => client.get("/explorer/stats").then((r) => r.data);
export const consensusHealth = () => client.get("/public/consensus/health").then((r) => r.data);
export const explorerSearch = (q) => client.get("/public/explorer/search", { params: { q } }).then((r) => r.data);
export const ledgerStats = () => client.get("/ledger/stats").then((r) => r.data);
export const ledgerVerify = (tx) => client.get(`/ledger/verify/${tx}`).then((r) => r.data);
export const pqcInfo = () => client.get("/pqc/info").then((r) => r.data);

// ── License (platform-level edition badge) ──
export const licenseStatusReal = () => client.get("/admin/license/status").then((r) => r.data);

// ── Leads (CRM · richiesta demo dal sito) ──
export const submitLead = (payload) => client.post("/leads", payload).then((r) => r.data);

// Public self-service 3-day TRIAL signup (isolated sidecar via /api/public/*).
// Creates tenant (TRIAL) + admin ML-DSA-65 identity and returns the downloadable keyfile.
export const trialSignup = (payload) => client.post("/public/trial-signup", payload).then((r) => r.data);
export const leadsUnread = () => client.get("/leads/unread-count").then((r) => r.data);
export const listLeads = () => client.get("/leads").then((r) => r.data);

// ── License & payments ──
export const licenseStatus = () => client.get("/license/status").then((r) => r.data);
export const licenseActivate = (license_key) => client.post("/license/activate", { license_key }).then((r) => r.data);
export const licenseCheckout = (plan) => client.post("/license/checkout", { plan, origin_url: window.location.origin }).then((r) => r.data);
export const licenseCheckoutStatus = (sid) => client.get(`/license/checkout/status/${sid}`).then((r) => r.data);

// Contact leads (submitContactLead) drive purchase requests — payment (bank transfer or crypto) is arranged personally.

// ── Super-admin (owner) ──
const adminHeaders = (token) => ({ headers: { Authorization: `Bearer ${token}` } });
export const adminSetupStatus = () => client.get("/admin/setup-status").then((r) => r.data);
export const adminSetup = (payload) => client.post("/admin/setup", payload).then((r) => r.data);
export const adminLogin = (payload) => client.post("/admin/login", payload).then((r) => r.data);
export const adminChangePassword = (token, payload) => client.post("/admin/change-password", payload, adminHeaders(token)).then((r) => r.data);
export const adminListLicenses = (token) => client.get("/admin/licenses", adminHeaders(token)).then((r) => r.data);
export const adminGenLicense = (token, pkg, note) => client.post("/admin/licenses", { package: pkg, note }, adminHeaders(token)).then((r) => r.data);
export const adminRevokeLicense = (token, key) => client.post(`/admin/licenses/${encodeURIComponent(key)}/revoke`, {}, adminHeaders(token)).then((r) => r.data);
export const adminUnbindLicense = (token, key) => client.post(`/admin/licenses/${encodeURIComponent(key)}/unbind`, {}, adminHeaders(token)).then((r) => r.data);
export const adminListTenants = (token) => client.get("/admin/tenants", adminHeaders(token)).then((r) => r.data);
export const adminStats = (token) => client.get("/admin/stats", adminHeaders(token)).then((r) => r.data);
export const admin2faStatus = (token) => client.get("/admin/2fa/status", adminHeaders(token)).then((r) => r.data);
export const admin2faEnroll = (token) => client.post("/admin/2fa/enroll", {}, adminHeaders(token)).then((r) => r.data);
export const admin2faVerify = (token, code) => client.post("/admin/2fa/verify", { code }, adminHeaders(token)).then((r) => r.data);
export const admin2faDisable = (token, code) => client.post("/admin/2fa/disable", { code }, adminHeaders(token)).then((r) => r.data);
// ── FIDO2 / WebAuthn passkeys (owner console) ──
export const adminWebauthnRegisterStart = (token) => client.post("/admin/webauthn/register/start", {}, adminHeaders(token)).then((r) => r.data);
export const adminWebauthnRegisterFinish = (token, payload) => client.post("/admin/webauthn/register/finish", payload, adminHeaders(token)).then((r) => r.data);
export const adminWebauthnList = (token) => client.get("/admin/webauthn/credentials", adminHeaders(token)).then((r) => r.data);
export const adminWebauthnDelete = (token, cred_id) => client.post("/admin/webauthn/credentials/delete", { cred_id }, adminHeaders(token)).then((r) => r.data);
export const adminWebauthnLoginStart = (email) => client.post("/admin/webauthn/login/start", { email }).then((r) => r.data);
export const adminWebauthnLoginFinish = (payload) => client.post("/admin/webauthn/login/finish", payload).then((r) => r.data);
// ── SSO · OpenID Connect (owner console) ──
export const adminSsoStatus = () => client.get("/admin/sso/status").then((r) => r.data);
export const adminSsoGetConfig = (token) => client.get("/admin/sso/config", adminHeaders(token)).then((r) => r.data);
export const adminSsoSaveConfig = (token, payload) => client.post("/admin/sso/config", payload, adminHeaders(token)).then((r) => r.data);
export const adminSsoTest = (token, issuer) => client.get("/admin/sso/test", { params: { issuer }, ...adminHeaders(token) }).then((r) => r.data);
export const ssoLoginUrl = () => `${API}/admin/sso/login`;
// ── SSO · SAML 2.0 (owner console) ──
export const adminSamlStatus = () => client.get("/admin/saml/status").then((r) => r.data);
export const adminSamlGetConfig = (token) => client.get("/admin/saml/config", adminHeaders(token)).then((r) => r.data);
export const adminSamlSaveConfig = (token, payload) => client.post("/admin/saml/config", payload, adminHeaders(token)).then((r) => r.data);
export const samlLoginUrl = () => `${API}/admin/saml/login`;
// ── LDAP / Active Directory (owner console) ──
export const adminLdapStatus = () => client.get("/admin/ldap/status").then((r) => r.data);
export const adminLdapGetConfig = (token) => client.get("/admin/ldap/config", adminHeaders(token)).then((r) => r.data);
export const adminLdapSaveConfig = (token, payload) => client.post("/admin/ldap/config", payload, adminHeaders(token)).then((r) => r.data);
export const adminLdapTest = (token) => client.post("/admin/ldap/test", {}, adminHeaders(token)).then((r) => r.data);
export const adminLdapSync = (token) => client.post("/admin/ldap/sync", {}, adminHeaders(token)).then((r) => r.data);
export const adminLdapLogin = (username, password) => client.post("/admin/ldap/login", { username, password }).then((r) => r.data);
// ── HSM / TPM · PKCS#11 (owner console) ──
export const adminHsmStatus = () => client.get("/admin/hsm/status").then((r) => r.data);
export const adminHsmGetConfig = (token) => client.get("/admin/hsm/config", adminHeaders(token)).then((r) => r.data);
export const adminHsmSaveConfig = (token, payload) => client.post("/admin/hsm/config", payload, adminHeaders(token)).then((r) => r.data);
export const adminHsmTest = (token) => client.post("/admin/hsm/test", {}, adminHeaders(token)).then((r) => r.data);
export const adminHsmSignTest = (token) => client.post("/admin/hsm/sign-test", {}, adminHeaders(token)).then((r) => r.data);
export const adminBackupStatus = (token) => client.get("/admin/backup/status", adminHeaders(token)).then((r) => r.data);
export const adminInstallations = (token) => client.get("/admin/installations", adminHeaders(token)).then((r) => r.data);
export const networkLive = () => client.get("/public/network/live").then((r) => r.data);
// ── Audit amministrativo (tamper-proof · catena hash SHA3-512) ──
export const adminGetAuditLog = (token, params = {}) => client.get("/admin/audit", { params, ...adminHeaders(token) }).then((r) => r.data);
export const adminVerifyAudit = (token) => client.get("/admin/audit/verify", adminHeaders(token)).then((r) => r.data);
export const adminAuditExportUrl = () => `${API}/admin/audit/export`;
export const adminAuditExport = (token, format = "cef", since = -1) =>
  client.get("/admin/audit/export", { params: { format, since }, ...adminHeaders(token), responseType: "text", transformResponse: [(d) => d] }).then((r) => r.data);

// Etichette leggibili + colore per gli eventi di audit.
export const AUDIT_EVENTS = {
  admin_login: { label: "Accesso admin", color: "var(--lt-primary)" },
  admin_login_failed: { label: "Accesso admin FALLITO", color: "#ff6b6b" },
  admin_password_change: { label: "Cambio password admin", color: "#ff9100" },
  license_generated: { label: "Licenza generata", color: "var(--lt-primary)" },
  license_revoked: { label: "Licenza revocata", color: "#ff6b6b" },
  license_unbound: { label: "Vincolo server sbloccato", color: "#ff9100" },
  license_install: { label: "Licenza installata", color: "var(--lt-primary)" },
  license_install_rejected: { label: "Installazione licenza rifiutata", color: "#ff6b6b" },
  user_created: { label: "Utente creato", color: "var(--lt-primary)" },
  lns_register: { label: "Identità registrata", color: "var(--lt-primary)" },
  user_role_changed: { label: "Ruolo utente modificato", color: "#ff9100" },
  api_key_created: { label: "API Key creata", color: "var(--lt-primary)" },
  api_key_revoked: { label: "API Key revocata", color: "#ff6b6b" },
  webauthn_registered: { label: "Passkey registrata", color: "var(--lt-primary)" },
  webauthn_removed: { label: "Passkey rimossa", color: "#ff9100" },
  sso_config_changed: { label: "Config SSO modificata", color: "#ff9100" },
  saml_config_changed: { label: "Config SAML modificata", color: "#ff9100" },
  ldap_config_changed: { label: "Config LDAP modificata", color: "#ff9100" },
  hsm_config_changed: { label: "Config HSM modificata", color: "#ff9100" },
  user_revoked: { label: "Utente revocato", color: "#ff6b6b" },
  certify_send: { label: "Certify · invio", color: "#7fb0ff" },
  certify_notary_seal: { label: "Certify · sigillo notarile", color: "#7fb0ff" },
  certify_delivery_receipt: { label: "Certify · ricevuta", color: "#7fb0ff" },
  pulse_anchor: { label: "Pulse · notarizzazione", color: "#7fb0ff" },
};
export const auditEventMeta = (ev) => AUDIT_EVENTS[ev] || { label: ev || "—", color: "#a3a3a3" };
export const downloadCsv = (rows, filename) => {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const cols = Object.keys(rows[0] || {});
  const csv = [cols.join(","), ...rows.map((r) => cols.map((c) => esc(r[c])).join(","))].join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
};

export const short = (s, head = 8, tail = 8) => {
  if (!s) return "—";
  if (s.length <= head + tail + 1) return s;
  return `${s.slice(0, head)}…${s.slice(-tail)}`;
};

export const fmtTime = (ts) => {
  if (!ts) return "—";
  try {
    return new Date(ts).toLocaleString("it-IT", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
  } catch { return ts; }
};

export const downloadJson = (obj, filename) => {
  const blob = new Blob([JSON.stringify(obj, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

// VETRINA AZIENDE (v7.9.2). La richiesta di preventivo è pubblica: chi arriva dal sito non
// ha un'identità Lattice, quindi il messaggio al proprietario lo scrive il nodo. Il
// generatore di installatori invece è dietro la sessione, e il nodo controlla che sia
// davvero il proprietario: qui non ci si fida del frontend.
export const richiediPreventivo = (dati) =>
  client.post("/public/aziende/preventivo", dati).then((r) => r.data);
// ACCOPPIAMENTO OTTICO (v8.1.3). Il browser apre il biglietto, guarda se il telefono ha
// consegnato la sua chiave ML-KEM-768 effimera, e deposita la busta sigillata e firmata.
// Tutte e tre stanno dietro la sessione sovrana: un biglietto lo apre solo chi è dentro.
export const accOpri = async (segretoHash) =>
  client.post("/public/accoppiamento/apri", { segreto_hash: segretoHash }, await scAuth()).then((r) => r.data);
export const accStato = async (id) =>
  client.get(`/public/accoppiamento/${id}/stato`, await scAuth()).then((r) => r.data);
export const accDeposita = async (id, busta, firma_hex, pk_hex) =>
  client.post(`/public/accoppiamento/${id}/busta`, { busta, firma_hex, pk_hex }, await scAuth()).then((r) => r.data);
export const accAnnulla = async (id) =>
  client.delete(`/public/accoppiamento/${id}`, await scAuth()).then((r) => r.data);

export const elencoRichieste = async () => client.get("/rete/aziende/richieste", await scAuth()).then((r) => r.data);
// v8.2.4 - ELIMINA e STATO dal sito: le stesse rotte del telefono, con la sessione del
// sidecar (`scAuth`). Il nodo cancella da entrambe le viste e manda la lapide a DE e FI.
export const eliminaRichiesta = async (id) =>
  client.delete(`/rete/aziende/richieste/${encodeURIComponent(id)}`, await scAuth()).then((r) => r.data);
export const statoRichiesta = async (id, stato) =>
  client.post(`/rete/aziende/richieste/${encodeURIComponent(id)}/stato`, { stato }, await scAuth()).then((r) => r.data);
export const elencoInstallatori = async () => client.get("/rete/installatori", await scAuth()).then((r) => r.data);
export const creaInstallatore = async (dati) => client.post("/rete/installatore", dati, await scAuth()).then((r) => r.data);

// GERARCHIA DELLA RETE (v7.9.2): proprietario, amministratore, membro. Il nodo decide su
// ogni rotta; queste chiamate servono solo a mostrare la verita a schermo.
export const mioGrado = async () => client.get("/rete/mio-grado", await scAuth()).then((r) => r.data);
export const gerarchiaRete = async () => client.get("/rete/gerarchia", await scAuth()).then((r) => r.data);
export const assegnaGrado = async (lns, grado) => client.post("/rete/grado", { lns, grado }, await scAuth()).then((r) => r.data);
// v8.2.7 - REVOCA SOVRANA. Il nodo decide se hai il potere: qui si chiede, non si suppone.
export const revocaLns = async (lns, motivo = "") =>
  client.post("/rete/lns/revoca", { lns, motivo }, await scAuth()).then((r) => r.data);
export const possoRevocare = async () => client.get("/rete/lns/posso-revocare", await scAuth()).then((r) => r.data);

// IL CLUB (v7.9.6). La porta e' chiusa: si entra con un codice invito monouso. Il codice
// in chiaro esiste una volta sola, nella risposta di creaInvito: nel nodo resta l'impronta.
// F2A SOVRANO (v7.9.6). L'accesso resta appeso a una sfida: il token esce solo quando il
// telefono ha FIRMATO «Autorizzo», e esce una volta sola.
export const f2aStato = (id) => client.get(`/public/f2a/stato/${encodeURIComponent(id)}`).then((r) => r.data);
// RIENTRO SOVRANO: telefono perduto. Il browser deriva la chiave dalle 12 frasi, controlla
// che combaci con quella scritta nel file identità e solo allora firma. Il nodo pretende
// anche il DID del file, e avvisa ogni dispositivo che qualcuno è rientrato da questa porta.
export const f2aRientro = async (lns_name, secret_key_hex, keyfile_did) => {
  const c = await sidecarChallenge(lns_name);
  const signature_hex = signChallenge(c.nonce_hex, secret_key_hex);
  return client.post("/public/f2a/rientro", { lns_name, nonce_id: c.nonce_id, signature_hex, keyfile_did }).then((r) => r.data);
};

export const verificaInvito = (codice) => client.get(`/public/invito/${encodeURIComponent(codice)}`).then((r) => r.data);
export const creaInvito = async (dati) => client.post("/rete/invito", dati, await scAuth()).then((r) => r.data);
// v8.1.0 — LA SQUADRA DELL'AZIENDA. La governano l'owner dell'azienda e i suoi
// amministratori (stessi poteri); il proprietario della rete può guardare qualunque
// azienda passando il `tenant_id`.
export const squadraAzienda = async (tenantId) =>
  client.get("/azienda/squadra", { ...(await scAuth()), params: tenantId ? { tenant_id: tenantId } : {} }).then((r) => r.data);
export const ruoloAzienda = async (dati) => client.post("/azienda/ruolo", dati, await scAuth()).then((r) => r.data);
export const elencoInviti = async () => client.get("/rete/inviti", await scAuth()).then((r) => r.data);
export const revocaInvito = async (id) => client.delete(`/rete/invito/${encodeURIComponent(id)}`, await scAuth()).then((r) => r.data);
